import * as XLSX from 'xlsx';
import { Project, ViewType } from '../types';

/**
 * Exports group-level notes from a project to Excel
 * Organizes notes by view type (product, brand, or sub-brand)
 */
export const exportGroupNotesToExcel = (project: Project) => {
  if (!project.groupNotes || Object.keys(project.groupNotes).length === 0) {
    alert('No group notes to export.');
    return;
  }

  const viewType = project.viewType || 'product';
  
  // Build the data array with headers
  const data: (string | number)[][] = [
    [
      'Group Type',
      'Group Name',
      'Note',
      'Product Count', // Only relevant for brand/sub-brand views
    ],
  ];

  // Helper to get group type label
  const getGroupTypeLabel = (): string => {
    switch (viewType) {
      case 'brand':
        return 'Brand';
      case 'sub-brand':
        return 'Sub-Brand';
      default:
        return 'Product';
    }
  };

  // Helper to count products in a group (for brand/sub-brand views)
  const getProductCountForGroup = (groupKey: string): number => {
    if (viewType === 'product') {
      return 1; // Each product is its own group
    }
    
    return project.products.filter(product => {
      if (viewType === 'brand') {
        return (product.brand || 'Unknown Brand') === groupKey;
      } else {
        return (product.subBrand || 'Unknown Sub-Brand') === groupKey;
      }
    }).length;
  };

  // Sort notes by group name for better readability
  const sortedGroupKeys = Object.keys(project.groupNotes).sort();

  // Add each note as a row
  sortedGroupKeys.forEach(groupKey => {
    const note = project.groupNotes![groupKey];
    if (note && note.trim()) {
      data.push([
        getGroupTypeLabel(),
        groupKey,
        note.trim(),
        getProductCountForGroup(groupKey),
      ]);
    }
  });

  // Create worksheet and workbook
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Group Notes');

  // Generate Excel file
  const excelBuffer = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });

  // Create blob and download
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${project.name.replace(/[^a-z0-9]/gi, '_')}_group_notes.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
